import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import backIcon from "../../assets/images/back-icon.svg";
import closeIcon from "../../assets/images/close.svg";
import menuIcon from "../../assets/images/menu.svg";
import { PERMISSIONS, USER_ROLE_TYPES } from "../../constants/Constants";
import { Routes } from "../../constants/RouteConstants";
import authContext from "../../context/authContext";
import ProfileImage from "./ProfileImage";
import Logout from "./Logout";
import { checkPermission, handleLogout, hasAnySubscription } from "../../utils/Helper";
import menuDashboard from "../../assets/images/menu/menu-dashboard.svg"
import household from "../../assets/images/menu/household.svg"
import chores from "../../assets/images/menu/chores.svg"
import expense from "../../assets/images/menu/expense.svg"
import opportunityCost from "../../assets/images/menu/opportunity-cost.svg"
import contribution from "../../assets/images/menu/contribution.svg"
import payment from "../../assets/images/menu/payment.svg"
import report from "../../assets/images/menu/report.svg"
import notification from "../../assets/images/menu/notification.svg"
import user from "../../assets/images/menu/account.svg"
import logout from "../../assets/images/menu/logout.svg"
import helpsupport from "../../assets/images/menu/help-support.svg"
import UpgradeToast from "./UpgradeToast";

type TopHeaderProps = {
  headerName?: string;
  onlyTitle?: boolean;
  showMenuOption?: boolean;
};

const TopHeader = ({ headerName, onlyTitle = false, showMenuOption = true }: TopHeaderProps) => {
  let profile: any = useContext(authContext);
  profile = profile?.data;

  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("");

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const drawerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggleMenu();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusableElements = drawerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex="0"]'
        );
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    if (drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex="0"]'
      );
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
        previouslyFocusedElement.focus();
      }
    };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      // Prevent horizontal scroll
      document.documentElement.style.overflowX = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflowX = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflowX = "";
    };
  }, [menuOpen]);

  const menu = [
    {
      name: "Dashboard",
      link: Routes.Dashboard,
      img: menuDashboard,
    },
    {
      name: "Notifications",
      link: Routes.Notifications,
      img: notification,
      requiredPermission: PERMISSIONS.NOTIFICATIONS,
    },
    {
      name: "Chores",
      link: Routes.Chores,
      img: chores,
      requiredPermission: PERMISSIONS.BASIC_CHORES,
    },
    {
      name: "Expenses",
      link: Routes.Expenses,
      img: expense,
      requiredPermission: PERMISSIONS.EXPENSES,
    },
    {
      name: "Opportunity Costs",
      link: Routes.OpportunityCost,
      img: opportunityCost,
      requiredPermission: PERMISSIONS.OPPORTUNITY_COST,
    },
    {
      name: "Contributions",
      link: Routes.Contributions,
      img: contribution,
    },
    {
      name: "Payments",
      link: Routes.Payment,
      img: payment,
      requiredPermission: PERMISSIONS.PAYEMTS,
    },
    {
      name: "Reports",
      link: Routes.Reports,
      img: report,
      requiredPermission: PERMISSIONS.REPORTS,
    },

    {
      name: "My Account",
      link: Routes.Profile,
      img: user,
    },
    ...((!profile?.hh_member?.member_type || profile?.role_type === USER_ROLE_TYPES?.GROUP_ADMIN)
      ? [{
        name: "Household",
        link: !hasAnySubscription(profile) ? '#' : Routes?.ManageHousehold,
        img: household,
      }]
      : []),
    {
      name: "Help & Support",
      link: Routes.Faqs,
      img: helpsupport,
    },
    {
      name: "Log out",
      link: Routes.Dashboard,
      img: logout,
      onClick: () => {
        setIsModalOpen(true);
        setMenuOpen(false);
      }
    },
  ];

  const handleMenuItemClick = (menu: string) => {
    setSelectedMenu(menu);
  };

  return (
    <>
      <header role="banner" className="top-header-container">
        <h1 className="head-title">
          {headerName ? (
            <div className="head-title-view">
              {!onlyTitle &&
                <button
                  type="button"
                  className="back-btn border-0 bg-transparent p-0"
                  onClick={() => navigate(-1)}
                  aria-label="Go back"
                  style={{ display: 'inline-flex', verticalAlign: 'middle' }}
                >
                  <img
                    src={backIcon}
                    alt=""
                    aria-hidden="true"
                    style={{ cursor: "pointer" }}
                  />
                </button>
              }
              {" "}{headerName}
            </div>
          ) : (
            <Link to={Routes?.Profile} className="dash-profile">
              <ProfileImage src={profile?.profile_pic} size={50} />
              <div className="dash-profile-name">
                <span>Hi</span> {profile?.name}
              </div>
            </Link>
          )}
          {showMenuOption &&
            <button className="menu-toggle" onClick={toggleMenu} aria-expanded={menuOpen} aria-label="Open Navigation Menu" aria-controls="navigation-drawer-top">
              <img src={menuIcon} alt="" aria-hidden="true" />
            </button>
          }
        </h1>
      </header>

      {/* Fixed side menu with proper positioning and viewport handling */}
      <div
        ref={drawerRef}
        id="navigation-drawer-top"
        role="dialog"
        aria-modal="true"
        aria-label="Main Navigation Menu"
        className={`side-menu ${menuOpen ? "open" : ""}`}
        style={{ visibility: menuOpen ? 'visible' : 'hidden' }}
      >
        <div className="side-menu-header">
          <div>Menu</div>
          <button className="close-btn" onClick={toggleMenu} aria-label="Close navigation menu">
            <img src={closeIcon} alt="" aria-hidden="true" />
          </button>
        </div>


        {/* User Profile Section */}
        <div className="user-profile-section">
          <div className="user-profile">
            <ProfileImage src={profile?.profile_pic} size={50} />
            <div className="user-info">
              <h3 className="user-name">{profile?.name || ""}</h3>
              <p className="user-email">{profile?.email || ""}</p>
            </div>
          </div>
        </div>

        <nav aria-label="Drawer Navigation">
          <ul className="menu-items">
          {menu.map((item, index) => {
            const hasSubscription = hasAnySubscription(profile);

            const hasPermissionAccess =
              !item.requiredPermission ||
              checkPermission(profile?.plan_perms, item.requiredPermission);

            const subscriptionRequiredItems = [
              "Chores",
              "Expenses",
              "Opportunity Costs",
              "Contributions",
              "Payments",
              "Reports",
              "Household"
            ];

            const isRestrictedItem = subscriptionRequiredItems.includes(item.name);

            const shouldBlockForHousehold = (item.name === "Contributions" || item.name === "Reports" || item.name === "Payments" || item.name === "Chores" || item.name === "Expenses" || item.name === "Opportunity Costs") && profile?.hh_member == null

            const isDisabled = (isRestrictedItem && !hasSubscription) || !hasPermissionAccess || shouldBlockForHousehold;

            const handleClick = (e: React.MouseEvent) => {

              // ✅ 0️⃣ Always allow logout
              if (item.name === "Log out") {
                if (item.onClick) item.onClick();
                return;
              }

              // ✅ 0.5️⃣ Always allow Help & Support
              if (item.name === "Help & Support") {
                handleMenuItemClick(item.name);
                toggleMenu();
                return;
              }

              if (item.name === "Dashboard" || item.name === "My Account") {
                handleMenuItemClick(item.name);
                toggleMenu();
                return;
              }
              // 1️⃣ No subscription
              if (!hasSubscription) {
                e.preventDefault();
                toast.dismiss();
                // toast.error("Please purchase a subscription to access these features.");
                toast.custom((t) => <UpgradeToast t={t} />, {
                  id: "upgrade-toast",
                  duration: 3000,
                });
                return;
              }

              // 2️⃣ Missing permission
              if (!hasPermissionAccess) {
                e.preventDefault();
                toast.dismiss();
                toast.custom((t) => <UpgradeToast t={t} />, {
                  id: "upgrade-toast",
                  duration: 3000,
                });
                return;
              }

              // 3️⃣ Household validation
              if (shouldBlockForHousehold) {
                e.preventDefault();
                toast.error("Please create a household first.");
                return;
              }

              handleMenuItemClick(item.name);
              toggleMenu();
            };

            return (
              <li
                key={index}
                className={`${selectedMenu === item.name ? "selected" : ""} ${isDisabled ? "pointer-events-none" : ""}`}
                style={{ pointerEvents: "auto", cursor: isDisabled ? "not-allowed" : "pointer" }}
                onClick={item.name === "Log out" ? undefined : handleClick}
              >
                {item.name === "Log out" ? (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      handleClick(e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClick(e as any);
                      }
                    }}
                  >
                    <img
                      src={item.img}
                      alt=""
                      aria-hidden="true"
                      className="menu-item-img"
                    />
                    {item.name}
                  </div>
                ) : (

                  <Link
                    to={isDisabled ? '#' : item.link}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick(e);
                    }}
                    className={isDisabled ? "disabled-link" : ""}
                  >
                    <img
                      src={item.img}
                      alt=""
                      aria-hidden="true"
                      className="menu-item-img"
                    />
                    {item.name}
                  </Link>
                )}
              </li>
            );
          })}
          </ul>
        </nav>
      </div>
      {isModalOpen &&
        <Logout handleLogout={() => handleLogout(navigate)} toggleModal={() => setIsModalOpen(false)} />
      }
      {menuOpen && <div className="backdrop" onClick={toggleMenu}></div>}
    </>
  );
};

export default TopHeader;